import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Chessboard } from 'react-chessboard';
import { BOARD_THEMES } from '../components/WorkingChessLogic/BoardStyle';

import { Chess } from 'chess.js';
import { TAGS, DIFFICULTY } from '../util/constants';

import { fetchGame } from '../util/api/game';
import { isValidGameId } from '../util/api/gameUtils';
import { getFeaturedGameId } from '../util/api/featuredContent';
import chessslogo from '../images/chessslogo.png'; // Import chessslogo
import { usePremium } from '../util/PremiumFilter';
import axios from 'axios';


// Display GameSummary card for a given game
const GameSummary = ({
  gameData,
  onClick,
  priorityTag = null,
  boardWidth = 250,
  maxContainerSize = 500,
  id = null,
  featured = false,
  preventAutoFetch = false
}) => {
  // Add state to store fetched game data when using id prop
  const [fetchedGameData, setFetchedGameData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [featuredId, setFeaturedId] = React.useState(null);
  // Add new state variables for hover status
  const [isDifficultyHovered, setIsDifficultyHovered] = React.useState(false);
  const [isScoreHovered, setIsScoreHovered] = React.useState(false);

  // Use the fetched data or the directly provided gameData
  const game = fetchedGameData || gameData;
  const history = useHistory();
  const location = useLocation();

  const { renderUserWithBadge, PREMIUM_TIERS } = usePremium();
  const [creatorData, setCreatorData] = useState(null);
  

  // Helper function to format the game score
  const formatScore = (score) => {
    if (isNaN(score)) {
      return 10; // Return 10 if score is NaN
    }
    if (score < 0) {
      return 0; // Return 0 if score is negative
    }
    if (score >= 1000) {
      const kValue = score / 1000;
      // Format to one decimal place, remove '.0' if it's an integer
      const formattedK = kValue.toFixed(1);
      const finalK = formattedK.endsWith('.0') ? formattedK.slice(0, -2) : formattedK;
      return `${finalK}k`; // Append 'k' for thousands
    }
    // Return the original score if between 0 and 999
    return score;
  };

  // Add state variables and hooks at the very top
  const [currentPosition, setCurrentPosition] = React.useState({});
  const [isHovering, setIsHovering] = React.useState(false);
  const [selectedTag, setSelectedTag] = React.useState(null);
  const [tagIndex, setTagIndex] = React.useState(0);
  const [validTags, setValidTags] = React.useState([]);
  const [isTagChanging, setIsTagChanging] = React.useState(false); // New state for animation
  const [customBoardPosition, setCustomBoardPosition] = React.useState(null); // For puzzle interactions
  const [invalidMoveAttempted, setInvalidMoveAttempted] = React.useState(false); // For puzzle feedback
  const [isTagHovered, setIsTagHovered] = React.useState(false); // Track hover state for the entire tag
  const [clickCount, setClickCount] = React.useState(0); // For triple click detection
  const [lastClickTime, setLastClickTime] = React.useState(0); // For triple click timing
  const [currentPuzzleMoveIndex, setCurrentPuzzleMoveIndex] = React.useState(0); // Track current move in puzzle solution
  const [puzzleCompleted, setPuzzleCompleted] = React.useState(false); // Track puzzle completion
  const [boardOrientation, setBoardOrientation] = React.useState('white'); // Track board orientation based on game POV
  const [reachedFinalPosition, setReachedFinalPosition] = React.useState(false); // Track when animation reaches the end

  // Get difficulty from game metadata with fallback to "All Levels"
  const difficulty = game?.difficulty || "All Levels";

  // Get the first letter of the difficulty for the icon
  const difficultyLetter = difficulty.charAt(0);

  // Get appropriate difficulty icon based on difficulty level
  const getDifficultyIcon = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'all levels':
        return <i className="fa-solid fa-chess-board"></i>;
      case 'beginner':
        return <i className="fa-solid fa-chess-pawn"></i>;
      case 'intermediate':
        return <i className="fa-solid fa-chess-knight"></i>;
      case 'advanced':
        return <i className="fa-solid fa-chess-rook"></i>;
      case 'master':
        return <i className="fa-solid fa-chess-queen"></i>;
      default:
        return <i className="fa-solid fa-chess-board"></i>; // Default icon
    }
  };

  // Helper function to handle special opening tags - simplify to avoid using animation state
  const getTagDisplay = (tag) => {
    if (!tag) return '';

    // Check if it's an Opening tag with format "Opening - Opening Name"
    if (tag.startsWith('Opening') && tag.includes(' - ')) {
      // Extract the opening name (part after the hyphen)
      const openingName = tag.split(' - ')[1];
      return openingName; // Just show the opening name, not "Opening - "
    }

    // For standard tags, use the label from TAGS constant
    return TAGS[tag]?.label || tag;
  };

  // Helper function to get tag styling
  const getTagStyle = (tag) => {
    // For special opening tags
    if (tag && tag.startsWith('Opening') && tag.includes(' - ')) {
      return {
        color: 'black',
        background: 'gold' // Blue background for opening tags
      };
    }

    // For standard tags
    return {
      color: TAGS[tag]?.color || '#FFFFFF',
      background: TAGS[tag]?.background || '#3066BE'
    };
  };

  // Create a robust checkForTag function similar to ViewGame.js
  const checkForTag = React.useCallback((gameData, tagToCheck) => {
    if (!gameData) return false;

    // Normalize tag for case-insensitive comparison
    tagToCheck = tagToCheck.toUpperCase();

    // Check if tags exist as an array
    if (Array.isArray(gameData.tags)) {
      if (gameData.tags.some(tag => typeof tag === 'string' && tag.toUpperCase() === tagToCheck)) {
        return true;
      }
    }

    // Check if game.tag exists (singular)
    if (typeof gameData.tag === 'string' && gameData.tag.toUpperCase() === tagToCheck) {
      return true;
    }

    // Check gameId for tag identifiers
    if (gameData.id && gameData.id.toUpperCase().includes(tagToCheck)) {
      return true;
    }

    // Check title or caption for tag identifiers
    if (gameData.title && gameData.title.toUpperCase().includes(tagToCheck)) {
      return true;
    }

    if (gameData.caption && gameData.caption.toUpperCase().includes(tagToCheck)) {
      return true;
    }

    // Check URL for query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlTag = urlParams.get('tag');
    if (urlTag && urlTag.toUpperCase() === tagToCheck) {
      return true;
    }

    return false;
  }, []);

  // Determine if this is a puzzle or challenge using the robust method
  const isPuzzleGame = React.useMemo(() => checkForTag(game, 'PUZZLE'), [game, checkForTag]);
  const isChallengeGame = React.useMemo(() => checkForTag(game, 'CHALLENGE'), [game, checkForTag]);
  const isInteractiveGame = React.useMemo(() => isPuzzleGame, [isPuzzleGame]);

  const playingInterval = React.useRef(null);
  const tagRotationInterval = React.useRef(null);
  const MOVE_TIME = 750;
  const TAG_ROTATION_TIME = 8000; // 8 seconds for tag rotation
  const TAG_ANIMATION_DURATION = 300; // Animation duration in ms

  // Get the current theme - moved up before any conditional returns
  const boardTheme = localStorage.getItem('boardTheme') || 'blue';
  const currentTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.blue;

  // Get custom colors from localStorage if the theme is 'custom'
  // Moved up before any conditional returns to fix the React Hook error
  const customColors = React.useMemo(() => {
    if (boardTheme === 'custom') {
      const savedCustomColors = localStorage.getItem('customColors');
      return savedCustomColors ? JSON.parse(savedCustomColors) : {
        dark: '#3066be',
        light: '#eeeed2'
      };
    }
    return null;
  }, [boardTheme]);

  // Modify the useEffect that fetches featured game data
  React.useEffect(() => {
    // Skip fetching if preventAutoFetch is true and we have gameData
    if (preventAutoFetch && gameData) {
      return;
    }

    if (featured && !id && !gameData) {
      const fetchFeatured = async () => {
        try {
          setIsLoading(true);
          const featuredGameId = await getFeaturedGameId();

          if (featuredGameId) {
            setFeaturedId(featuredGameId);

            // Now fetch the actual game data
            try {
              const data = await fetchGame(featuredGameId);
              setFetchedGameData(data);
              setIsLoading(false);
            } catch (err) {
              console.error('Error fetching featured game data:', err);
              setError('Failed to load featured game data');
              setIsLoading(false);
            }
          } else {
            setError('No featured game set');
            setIsLoading(false);
          }
        } catch (err) {
          console.error('Error fetching featured game ID:', err);
          setError('Failed to determine featured game');
          setIsLoading(false);
        }
      };

      fetchFeatured();
    }
  }, [featured, id, gameData, preventAutoFetch]);

  // Modify the useEffect that fetches game data by ID
  React.useEffect(() => {
    // Skip fetching if preventAutoFetch is true and we have gameData
    if (preventAutoFetch && gameData) {
      return;
    }

    // Only fetch if id is provided and gameData is not directly provided
    if (id && !gameData) {
      // Validate the game ID format
      if (!isValidGameId(id)) {
        setError('Invalid game ID format');
        return;
      }

      setIsLoading(true);
      setError(null);

      fetchGame(id)
        .then(data => {
          setFetchedGameData(data);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error fetching game data:', err);
          setError('Failed to load game data');
          setIsLoading(false);
        });
    }
  }, [id, gameData, preventAutoFetch]);

  // Calculate position object from game data - MOVED UP before it's used
  // This will return an empty object if game is undefined
  const calculatePositionObject = React.useMemo(() => {
    if (!game) return {};

    const fen = Object.hasOwn(game, 'fen')
      ? game.fen
      : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

    // Ensure moves is an array
    let moves = [];
    if (Object.hasOwn(game, 'moves')) {
      if (Array.isArray(game.moves)) {
        moves = game.moves;
      } else if (typeof game.moves === 'string') {
        try {
          moves = JSON.parse(game.moves);
          if (!Array.isArray(moves)) moves = [];
        } catch (e) {
          console.error('Error parsing moves string:', e);
          moves = [];
        }
      }
    }

    // Create Chess object with fen and make all moves
    const gamePosition = new Chess(fen);
    if (Array.isArray(moves)) {
      moves.forEach((moveObject) => {
        try {
          gamePosition.move(moveObject.notation);
        } catch (err) {
          console.error('Invalid move:', moveObject.notation, err);
        }
      });
    }

    // Get final position
    return gamePosition
      .board()
      .reduce((squareList, row, rowIndex) => {
        row.forEach((piece, colIndex) => {
          if (piece) {
            const file = String.fromCharCode(97 + colIndex);
            const rank = 8 - rowIndex;
            const square = `${file}${rank}`;
            const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
            squareList[square] = pieceCode;
          }
        });
        return squareList;
      }, {});
  }, [game]);

  // Updated useEffect for tag selection with priority tag handling
  React.useEffect(() => {
    if (!game) return; // Early return if game is undefined

    // Filter valid tags, including "Opening - X" format tags
    const filteredTags = game.tags ? game.tags.filter(tag =>
      Object.hasOwn(TAGS, tag) || (tag.startsWith('Opening') && tag.includes(' - '))
    ) : [];

    setValidTags(filteredTags);

    if (filteredTags.length > 0) {
      // If we have a priority tag and it's in the game's tags, select it first
      if (priorityTag && filteredTags.includes(priorityTag)) {
        setSelectedTag(priorityTag);
        // Find the index of the priority tag to start rotation from there
        const priorityIndex = filteredTags.indexOf(priorityTag);
        setTagIndex(priorityIndex);
      } else {
        // Otherwise, use the first tag
        setSelectedTag(filteredTags[0]);
        setTagIndex(0);
      }

      // Only set up rotation if there are multiple tags
      if (filteredTags.length > 1) {
        // Clear any existing interval
        if (tagRotationInterval.current) {
          clearInterval(tagRotationInterval.current);
        }

        // Set up tag rotation
        tagRotationInterval.current = setInterval(() => {
          // Trigger animation before changing tag
          setIsTagChanging(true);

          // After a short delay, update the tag index
          setTimeout(() => {
            setTagIndex(prevIndex => (prevIndex + 1) % filteredTags.length);
          }, TAG_ANIMATION_DURATION / 2); // Change halfway through the animation
        }, TAG_ROTATION_TIME);
      }
    }

    // Cleanup function
    return () => {
      if (tagRotationInterval.current) {
        clearInterval(tagRotationInterval.current);
      }
    };
  }, [game, priorityTag]); // Added priorityTag as dependency

  // Add a new useEffect to update the selected tag when tagIndex changes
  React.useEffect(() => {
    if (validTags.length > 0) {
      setSelectedTag(validTags[tagIndex]);

      // Reset the animation state after the tag has changed
      if (isTagChanging) {
        setTimeout(() => {
          setIsTagChanging(false);
        }, TAG_ANIMATION_DURATION / 2);
      }
    }
  }, [tagIndex, validTags, isTagChanging]);

  // Add useEffect for autoplay logic with conditional for puzzles
  React.useEffect(() => {
    // Initialize with the calculated position when not hovering
    if (!game || !isHovering) {
      if (game) setCurrentPosition(calculatePositionObject);
      setReachedFinalPosition(false); // Reset final position flag when not hovering
      clearTimeout(playingInterval.current);
      return;
    }

    // Skip autoplay for puzzles to not reveal the solution
    if (isPuzzleGame) {
      return;
    }

    // Reset the final position flag when starting a new hover animation
    setReachedFinalPosition(false);

    // Get the game's initial FEN from the game data
    const initialFen = Object.hasOwn(game, 'fen')
      ? game.fen
      : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

    // Start from the game's initial position, not the default new game position
    const tempGame = new Chess(initialFen);

    // Set the initial board position when hovering starts
    setCurrentPosition(
      tempGame.board().reduce((squareList, row, rowIndex) => {
        row.forEach((piece, colIndex) => {
          if (piece) {
            const file = String.fromCharCode(97 + colIndex);
            const rank = 8 - rowIndex;
            const square = `${file}${rank}`;
            const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
            squareList[square] = pieceCode;
          }
        });
        return squareList;
      }, {})
    );

    // Initialize move counter for the playback sequence
    let moveIndex = 0;

    // Create a properly formatted moves array from game data
    let moves = [];
    if (game.moves) {
      if (Array.isArray(game.moves)) {
        moves = game.moves;
      } else if (typeof game.moves === 'string') {
        try {
          moves = JSON.parse(game.moves);
          if (!Array.isArray(moves)) moves = [];
        } catch (e) {
          console.error('Error parsing moves string:', e);
          moves = [];
        }
      }
    }

    const playNextMove = () => {
      if (moveIndex < moves.length) {
        try {
          // Extract the move notation from the move object
          const moveNotation = moves[moveIndex].notation;

          // Make the move on our local chess game
          tempGame.move(moveNotation);

          // Update the board display with the new position
          setCurrentPosition(
            tempGame.board().reduce((squareList, row, rowIndex) => {
              row.forEach((piece, colIndex) => {
                if (piece) {
                  const file = String.fromCharCode(97 + colIndex);
                  const rank = 8 - rowIndex;
                  const square = `${file}${rank}`;
                  const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
                  squareList[square] = pieceCode;
                }
              });
              return squareList;
            }, {})
          );

          // Increment move index for next iteration
          moveIndex++;

          // Check if we've reached the final position
          if (moveIndex >= moves.length) {
            setReachedFinalPosition(true);
          }

          // Schedule the next move with a timeout
          playingInterval.current = setTimeout(playNextMove, MOVE_TIME);
        } catch (err) {
          console.error('Error playing move:', err, {
            moveIndex,
            move: moves[moveIndex]
          });
          setIsHovering(false);
        }
      } else {
        // End of moves reached, set final position flag
        setReachedFinalPosition(true);

        // Keep hover state active for a few seconds to show the final position with highlights and arrows
        // instead of immediately ending the hover
        if (arrows.length > 0 || Object.keys(highlightedSquares).length > 0) {
          // If we have arrows or highlights, keep the hover state for a few more seconds
          playingInterval.current = setTimeout(() => {
            // Only end hover if we're still in the same hover session
            setIsHovering(false);
          }, MOVE_TIME * 4); // Extend the display of final position with highlights
        } else {
          // If no highlights/arrows, end hover normally
          setIsHovering(false);
        }
      }
    };

    // Start the first move after the initial delay
    playingInterval.current = setTimeout(playNextMove, MOVE_TIME);

    // Cleanup function to clear the timeout when component unmounts or hovering stops
    return () => {
      clearTimeout(playingInterval.current);
    };
  }, [isHovering, game, calculatePositionObject, isPuzzleGame]);

  // Add a new effect to initialize the custom board position for puzzles/challenges
  React.useEffect(() => {
    if (game && isInteractiveGame) {


      const initialFen = Object.hasOwn(game, 'fen')
        ? game.fen
        : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

      setCustomBoardPosition(new Chess(initialFen));
    }
  }, [game, isInteractiveGame, isPuzzleGame, isChallengeGame]);

  // // Add debug logs for the tag detection
  // React.useEffect(() => {
  //   if (game) {
  //     console.log('GameSummary - Tag detection:', {
  //       gameId: game.id,
  //       tags: game.tags,
  //       isPuzzleGame,
  //       isChallengeGame,
  //       isInteractiveGame
  //     });
  //   }
  // }, [game, isPuzzleGame, isChallengeGame, isInteractiveGame]);

  // Set board orientation based on game.pov
  React.useEffect(() => {
    if (game) {
      // Use the same logic as ViewGame to determine orientation
      // game.pov === true or undefined means white orientation
      // game.pov === false means black orientation
      const orientation = game.pov === false ? 'black' : 'white';
      // console.log('GameSummary - Setting board orientation:', {
      //   pov: game.pov,
      //   orientation
      // });
      setBoardOrientation(orientation);
    }
  }, [game]);

  // Handle interactive board piece movement
  const onPieceDrop = (sourceSquare, targetSquare) => {
    if (!isInteractiveGame || !customBoardPosition) return false;

    try {
      // For puzzles, check if the move matches the solution
      if (isPuzzleGame) {
        // Parse moves properly from different possible formats
        let parsedMoves = [];
        if (game.moves) {
          if (Array.isArray(game.moves)) {
            parsedMoves = game.moves;
          } else if (typeof game.moves === 'string') {
            try {
              // Try to parse as JSON
              const jsonMoves = JSON.parse(game.moves);
              if (Array.isArray(jsonMoves)) {
                parsedMoves = jsonMoves;
              } else {
                // Fallback: treat the string as PGN and create move objects
                parsedMoves = game.moves.split(' ')
                  .filter(move => move.trim().length > 0)
                  .map(notation => ({ notation }));
              }
            } catch (e) {
              console.error('Error parsing moves string:', e);
              // Fallback: treat the string as PGN and create move objects
              parsedMoves = game.moves.split(' ')
                .filter(move => move.trim().length > 0)
                .map(notation => ({ notation }));
            }
          }
        }

        // Full debug logging with properly parsed moves


        // Try to make the move
        const moveAttempt = customBoardPosition.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: 'q', // Default to queen for simplicity
        });

        // If move is valid in chess rules
        if (moveAttempt) {


          // Get the expected move (if available)
          const expectedMove = parsedMoves.length > currentPuzzleMoveIndex ?
            parsedMoves[currentPuzzleMoveIndex] : null;

          // Check if we have an expected move and the user's move matches it
          // This now properly checks against the expected move
          let moveIsCorrect = false; // Default to false - move must match solution
          if (expectedMove && expectedMove.notation) {


            // Compare the player's move with the expected move
            // First, try direct notation comparison
            if (moveAttempt.san === expectedMove.notation) {
              moveIsCorrect = true;
            }
            // Try comparing UCI format (from-to)
            else if (expectedMove.notation.length >= 4) {
              const expectedFrom = expectedMove.notation.substring(0, 2).toLowerCase();
              const expectedTo = expectedMove.notation.substring(2, 4).toLowerCase();

              if (sourceSquare.toLowerCase() === expectedFrom &&
                targetSquare.toLowerCase() === expectedTo) {
                moveIsCorrect = true;
              }
            }
            // Try using chess.js to validate the move
            else {
              try {
                // Create a temporary chess instance with the current position
                const tempChess = new Chess(customBoardPosition.fen());

                // Try to make the expected move and get its details
                const expectedMoveResult = tempChess.move(expectedMove.notation, { sloppy: true });

                if (expectedMoveResult) {
                  // Compare the from and to squares
                  if (expectedMoveResult.from === sourceSquare &&
                    expectedMoveResult.to === targetSquare) {
                    moveIsCorrect = true;
                  }

                  // console.log('Move comparison details:', {
                  //   expectedFrom: expectedMoveResult.from,
                  //   expectedTo: expectedMoveResult.to,
                  //   playerFrom: sourceSquare,
                  //   playerTo: targetSquare,
                  //   matches: expectedMoveResult.from === sourceSquare &&
                  //     expectedMoveResult.to === targetSquare
                  // });
                }
              } catch (e) {
                console.error('Error validating expected move:', e);
              }
            }

            
          }

          if (moveIsCorrect) {
            // Update the board position
            setCurrentPosition(
              customBoardPosition.board().reduce((squareList, row, rowIndex) => {
                row.forEach((piece, colIndex) => {
                  if (piece) {
                    const file = String.fromCharCode(97 + colIndex);
                    const rank = 8 - rowIndex;
                    const square = `${file}${rank}`;
                    const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
                    squareList[square] = pieceCode;
                  }
                });
                return squareList;
              }, {})
            );

            // Check if this was the final move in the puzzle
            const isFinalMove = currentPuzzleMoveIndex >= parsedMoves.length - 1;

            // If there's a second move (opponent's response), play it automatically after a short delay
            if (!isFinalMove && currentPuzzleMoveIndex + 1 < parsedMoves.length) {
              setTimeout(() => {
                try {
                  const opponentMove = parsedMoves[currentPuzzleMoveIndex + 1]; // Get opponent's move

                  // Make sure the opponent move exists and has notation
                  if (!opponentMove || !opponentMove.notation) {
                    console.error('Missing opponent move data:', opponentMove);
                    // Still increment the index to mark progress
                    setCurrentPuzzleMoveIndex(prevIndex => prevIndex + 1);
                    return;
                  }

                  

                  // Try to make the opponent move
                  const moveResult = customBoardPosition.move(opponentMove.notation);

                  if (!moveResult) {
                    console.error('Failed to make opponent move:', opponentMove.notation);
                    // Try alternative notation formats
                    const alternativeNotations = [
                      // Try source-target format (e.g., "e2e4")
                      opponentMove.notation.substring(0, 2) + opponentMove.notation.substring(2, 4),
                      // Try UCI format if it's algebraic
                      opponentMove.notation
                    ];

                    let moveSucceeded = false;
                    for (const altNotation of alternativeNotations) {
                      if (altNotation !== opponentMove.notation) {
                        const altResult = customBoardPosition.move(altNotation, { sloppy: true });
                        if (altResult) {
                          
                          moveSucceeded = true;
                          break;
                        }
                      }
                    }

                    if (!moveSucceeded) {
                      // If all attempts fail, still increment the index to avoid getting stuck
                      setCurrentPuzzleMoveIndex(prevIndex => prevIndex + 1);
                      return;
                    }
                  }

                  // Update the board position again after opponent's move
                  setCurrentPosition(
                    customBoardPosition.board().reduce((squareList, row, rowIndex) => {
                      row.forEach((piece, colIndex) => {
                        if (piece) {
                          const file = String.fromCharCode(97 + colIndex);
                          const rank = 8 - rowIndex;
                          const square = `${file}${rank}`;
                          const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
                          squareList[square] = pieceCode;
                        }
                      });
                      return squareList;
                    }, {})
                  );

                  // Update move index by 2 (player move + computer response)
                  setCurrentPuzzleMoveIndex(prevIndex => {
                    const newIndex = prevIndex + 2;
                    
                    return newIndex;
                  });

                  // Check for puzzle completion at the end of the sequence
                  if (currentPuzzleMoveIndex + 2 >= parsedMoves.length) {
                    // console.log('End of puzzle sequence reached', {
                    //   currentIndex: currentPuzzleMoveIndex,
                    //   newIndex: currentPuzzleMoveIndex + 2,
                    //   totalMoves: parsedMoves.length
                    // });
                  }
                } catch (err) {
                  console.error('Error playing opponent move:', err);
                  // Still increment the index by 1 so we don't get stuck
                  setCurrentPuzzleMoveIndex(prevIndex => prevIndex + 1);
                }
              }, 500); // 500ms delay before opponent's move
            }
            else {
              // If there's no second move or this is the final move, still increment the move index
              setCurrentPuzzleMoveIndex(prevIndex => {
                const newIndex = prevIndex + 1;
                // console.log(`Final move index updated: ${prevIndex} -> ${newIndex}`, {
                //   isFinalMove,
                //   totalMoves: parsedMoves.length
                // });
                return newIndex;
              });

              // Log that this appears to be the final move
              if (isFinalMove) {
                // console.log('Final puzzle move completed by player', {
                //   moveIndex: currentPuzzleMoveIndex,
                //   totalMoves: parsedMoves.length,
                //   moveType: currentPuzzleMoveIndex % 2 === 0 ? 'player move' : 'opponent move'
                // });
              }
            }

            return true;
          } else {
            // If the move doesn't match the expected one, show feedback
            setInvalidMoveAttempted(true);
            setTimeout(() => setInvalidMoveAttempted(false), 750);

            // Reset the position
            customBoardPosition.undo();
            setCurrentPosition(
              customBoardPosition.board().reduce((squareList, row, rowIndex) => {
                row.forEach((piece, colIndex) => {
                  if (piece) {
                    const file = String.fromCharCode(97 + colIndex);
                    const rank = 8 - rowIndex;
                    const square = `${file}${rank}`;
                    const pieceCode = `${piece.color === 'w' ? 'w' : 'b'}${piece.type.toUpperCase()}`;
                    squareList[square] = pieceCode;
                  }
                });
                return squareList;
              }, {})
            );
            return false;
          }
        }

        return false;
      }

      return false;
    } catch (error) {
      console.error('Error handling piece drop:', error);
      return false;
    }
  };

  // Reset puzzle move index when game changes
  React.useEffect(() => {
    if (isPuzzleGame) {
      setCurrentPuzzleMoveIndex(0);
      setPuzzleCompleted(false);
    }
  }, [game, isPuzzleGame]);

  // Check if puzzle is completed
  React.useEffect(() => {
    if (isPuzzleGame && game?.moves) {
      // Parse moves properly
      let parsedMoves = [];
      if (game.moves) {
        if (Array.isArray(game.moves)) {
          parsedMoves = game.moves;
        } else if (typeof game.moves === 'string') {
          try {
            // Try to parse as JSON
            const jsonMoves = JSON.parse(game.moves);
            if (Array.isArray(jsonMoves)) {
              parsedMoves = jsonMoves;
            } else {
              // Fallback: treat the string as PGN and create move objects
              parsedMoves = game.moves.split(' ')
                .filter(move => move.trim().length > 0)
                .map(notation => ({ notation }));
            }
          } catch (e) {
            // Fallback: treat the string as PGN and create move objects
            parsedMoves = game.moves.split(' ')
              .filter(move => move.trim().length > 0)
              .map(notation => ({ notation }));
          }
        }
      }

      const totalMoves = parsedMoves.length;

      // // For debugging
      // console.log('Puzzle completion check:', {
      //   currentPuzzleMoveIndex,
      //   totalMoves,
      //   movePattern: totalMoves % 2 === 0 ? 'even (ends with opponent)' : 'odd (ends with player)'
      // });

      // Wait for player to complete the last move in all cases
      let isComplete = false;

      // Case 1: Puzzle has odd number of moves (ends with player move)
      if (totalMoves % 2 === 1) {
        // Must reach the exact end of moves list
        isComplete = currentPuzzleMoveIndex >= totalMoves;
      }
      // Case 2: Puzzle has even number of moves (ends with opponent move)
      else {
        // After opponent's final automated move
        isComplete = currentPuzzleMoveIndex >= totalMoves;
      }

      // Additional validation to prevent premature completion
      // Make sure we're not marking complete after an opponent's automated move
      if (isComplete && totalMoves % 2 === 1) {
        // For odd-length puzzles, we should be at an odd index
        if (currentPuzzleMoveIndex % 2 === 0 && currentPuzzleMoveIndex < totalMoves) {
          isComplete = false;
          
        }
      }

      // Only set completed if conditions are met and we've made at least one move
      if (isComplete && currentPuzzleMoveIndex > 0) {
        // console.log('Puzzle completed!', {
        //   currentPuzzleMoveIndex,
        //   totalMoves,
        //   condition: 'reached final move'
        // });
        setPuzzleCompleted(true);
      }
    }
  }, [isPuzzleGame, game, currentPuzzleMoveIndex]);

  // Handle puzzle completion click
  const handlePuzzleCompletionClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      // Open the full game with the complete state
      onClick(e, { openGameSettings: true, puzzleComplete: true });
    }
  };

  // After the game constant definition, add a useEffect for debugging
  React.useEffect(() => {
    // This will help us understand what's happening with the data
    if (preventAutoFetch && gameData && gameData._fetchComplete) {
    }
  }, [gameData, preventAutoFetch]);

  // Extract creator info from game data early
  const getCreatorHandle = (gameData) => {
    if (!gameData) return null;
    return Object.hasOwn(gameData, 'creatorHandle') ? gameData.creatorHandle :
      (Object.hasOwn(gameData, 'creator') && typeof gameData.creator === 'string' && !gameData.creator.includes(' ') ?
        gameData.creator : 'Anonymous');
  };

  // Get creator handle safely
  const creator = React.useMemo(() => getCreatorHandle(game), [game]);

  // Fetch creator's data including premium status
  useEffect(() => {
    const fetchCreatorData = async () => {
      if (creator) {
        try {
          const response = await axios.get(`/user/${creator}`);
          if (response.data && response.data.user) {
            setCreatorData(response.data.user);
          }
        } catch (error) {
          console.error(`Error fetching creator data: ${error}`);
        }
      }
    };

    fetchCreatorData();
  }, [creator]);

  // Modify the loading state check to respect the _fetchComplete flag
  // Display loading state with improved UI
  if (isLoading && (!gameData || !gameData._fetchComplete)) {
    
    return (
      <div style={{
        color: 'gold',
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#222',
        borderRadius: '10px',
        width: `${boardWidth}px`,
        height: `${boardWidth}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>
          <div style={{ fontSize: '24px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
            <img
              src={chessslogo}
              alt="Loading chess"
              style={{
                width: '50px',
                height: 'auto',
                animation: 'spin 2s linear infinite'
              }}
            />
          </div>
          <p>Loading game...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  // For missing/deleted games, return null instead of showing error UI
  // This prevents blank game cards from appearing in bookmarks and notifications
  if (error) {
    // Check if it's a game not found error (404, deleted, etc.)
    if (error.includes('not found') || error.includes('deleted') || error.includes('404') || error.includes('Game not found')) {
      return null; // Don't render anything for missing games
    }
    
    // For other errors (network, auth, etc.), still show error UI
    return (
      <div style={{
        color: 'red',
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#222',
        borderRadius: '10px',
        width: `${boardWidth}px`,
        height: `${boardWidth}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}>
        <div>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>
            <i className="fa-solid fa-triangle-exclamation"></i>
          </div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Check for not found error - return null to hide missing games
  if (fetchedGameData && fetchedGameData.isNotFound) {
    return null; // Don't render anything for missing/deleted games
  }

  // Check for auth error specifically, but don't show an error UI
  // Just render a basic placeholder for game data
  if (fetchedGameData && fetchedGameData.isAuthError) {
    return (
      <div style={{
        color: 'white',
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#222',
        borderRadius: '10px',
        width: `${boardWidth}px`,
        height: `${boardWidth}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        cursor: 'pointer'
      }}
        onClick={onClick}
      >
        <div>
          <img
            src={chessslogo}
            alt="Chess logo"
            style={{ width: '50px', height: 'auto', marginBottom: '15px' }}
          />
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'gold' }}>
            Chess Game Preview
          </div>
          <p style={{ marginTop: '10px', fontSize: '14px' }}>
            Click to view this game
          </p>
        </div>
      </div>
    );
  }

  // Early return for undefined game - return null instead of error UI
  // This prevents blank game cards from appearing in bookmarks and notifications
  if (game === undefined) {
    return null; // Don't render anything for undefined games
  }

  // Set game data
  // const creator = Object.hasOwn(game, 'creatorHandle') ? game.creatorHandle :
  //   (Object.hasOwn(game, 'creator') && typeof game.creator === 'string' && !game.creator.includes(' ') ?
  //     game.creator : 'Anonymous');

  let caption = Object.hasOwn(game, 'caption') ? game.caption : 'No caption';
  const tags = Object.hasOwn(game, 'tags') ? game.tags : [];

  const moves = Object.hasOwn(game, 'moves') ? game.moves : [];
  const gameScore = Object.hasOwn(game, 'score') ? game.score : 0;
  const adjustedScore = formatScore(gameScore); // Use the helper function



  // More robust handling of lastMove and its properties
  let lastMove = {};
  if (Array.isArray(moves) && moves.length > 0) {
    // Get the last move from the array
    lastMove = moves[moves.length - 1];
  } else if (typeof moves === 'string') {
    try {
      // Try to parse if it's a JSON string
      const parsedMoves = JSON.parse(moves);
      if (Array.isArray(parsedMoves) && parsedMoves.length > 0) {
        lastMove = parsedMoves[parsedMoves.length - 1];
      }
    } catch (e) {
      console.error('Error parsing moves string:', e);
    }
  }


  const fen = Object.hasOwn(game, 'fen')
    ? game.fen
    : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

  // Extract arrows and highlights safely
  const arrows = lastMove && typeof lastMove === 'object' && lastMove.arrows ? lastMove.arrows : [];
  const highlightedSquares = lastMove && typeof lastMove === 'object' && lastMove.highlightedSquares ? lastMove.highlightedSquares : {};



  const showCoordinates = localStorage.getItem('showCoordinates') === 'true';

  // Modified onClick handler for board container
  const handleBoardContainerClick = (e) => {
    // Only prevent navigation for puzzle boards, challenges behave like normal games
    if (isPuzzleGame && e.target.closest('.chess-board')) {
      e.stopPropagation();
      
      return false; // Return false to prevent default behavior
    }

    // Otherwise, proceed with normal onClick behavior
    if (onClick) {
      onClick(e);
    }
  };

  // Special handler for the chess board with triple-click detection
  const handleChessBoardClick = (e) => {
    const currentTime = new Date().getTime();

    // Only handle special clicks for puzzles
    if (isPuzzleGame) {
      e.stopPropagation();

      // Check if this is a rapid click (within 500ms of previous)
      if (currentTime - lastClickTime < 500) {
        setClickCount(prevCount => prevCount + 1);

        // If we've reached 3 clicks (triple click), open the game
        if (clickCount >= 2) {
          
          setClickCount(0);
          if (onClick) {
            // Pass additional info to show that puzzle view should open
            onClick(e, { fromTripleClick: true });
          }
          return;
        }
      } else {
        // Reset click count if too much time passed
        setClickCount(1);
      }

      setLastClickTime(currentTime);
      return false;
    }
  };

  // Handlers for the entire tag hover state
  const handleTagMouseEnter = () => {
    setIsTagHovered(true);
  };

  const handleTagMouseLeave = () => {
    setIsTagHovered(false);
  };

  // Handle tag click to navigate to ViewGame
  const handleTagClick = (e) => {
    if (onClick) {
      // For puzzles, pass additional info to show right interface
      if (isPuzzleGame) {
        onClick(e, { fromTagClick: true });
      } else {
        onClick(e);
      }
    }
  };

  // Update the keyframes in the useEffect
  const slideTextKeyframes = `
    @keyframes slideText {
      0%, 5% { transform: translate(-50%, -50%) translateX(100%); }
      45%, 55% { transform: translate(-50%, -50%) translateX(-100%); }
      95%, 100% { transform: translate(-50%, -50%) translateX(100%); }
    }
  `;

  return (
    <div
      className=""
      onClick={handleBoardContainerClick}
    >
      <div
        className="card-summary-inside"
        style={{
          maxHeight: '800px',
          width: `${boardWidth + 10}px`,
          maxWidth: maxContainerSize,
          position: 'relative', // Add position relative for absolute positioning
        }}
      >

        <div
          style={{
            color: '#ffd700',
            fontSize: '24px',
            fontWeight: 'bold',
            paddingBottom: '5px',
            marginLeft: '5px',
            marginRight: '5px',
            width: `${boardWidth}px`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            borderBottom: '1px solid #ffd700',
            textAlign: 'center',
            margin: '0 auto',
          }}
        >
          {creatorData ? renderUserWithBadge(creator, creatorData.premium) : renderUserWithBadge(creator)}
        </div>
        <div
          style={{
            color: 'white',
            fontSize: '20px',
            paddingBottom: '10px',
            paddingTop: '5px',
            width: `${boardWidth}px`,
            margin: '0 auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
            fontWeight: 'lighter',
          }}
        >
          {caption}
        </div>

        {/* Create a board of the final position that is not interactive */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative', // Make sure it's relative for positioning
          }}
        >
          <div
            onMouseEnter={() => !isPuzzleGame && setIsHovering(true)}
            onMouseLeave={() => !isPuzzleGame && setIsHovering(false)}
            onClick={handleChessBoardClick}
            className="chess-board"
            style={{
              position: 'relative',
              zIndex: isPuzzleGame ? 10 : 1, // Higher z-index for interactive boards
            }}
          >
            {/* Show feedback for invalid moves in puzzles */}
            {invalidMoveAttempted && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 0, 0, 0.7)',
                color: 'white',
                padding: '10px',
                borderRadius: '5px',
                zIndex: 100,
                pointerEvents: 'none'
              }}>
                Try another move
              </div>
            )}

            {/* Show puzzle completion notification */}
            {puzzleCompleted && (
              <div
                onClick={handlePuzzleCompletionClick}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 200, 0, 0.9)',
                  color: 'white',
                  padding: '15px 20px',
                  borderRadius: '8px',
                  zIndex: 100,
                  textAlign: 'center',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '5px' }}>
                  Puzzle Complete!
                </div>
                <div style={{ fontSize: '14px' }}>
                  Click For More Options
                </div>
              </div>
            )}

            <Chessboard
              customBoardStyle={{
                cursor: isPuzzleGame ? 'grab' : 'pointer',
              }}
              boardWidth={boardWidth}
              boardOrientation={boardOrientation}
              position={isPuzzleGame && customBoardPosition ?
                customBoardPosition.fen() : currentPosition}
              customDarkSquareStyle={boardTheme === 'custom' && customColors
                ? { background: customColors.dark }
                : currentTheme.dark}
              customLightSquareStyle={boardTheme === 'custom' && customColors
                ? { background: customColors.light }
                : currentTheme.light}
              // Arrows drawn in final position - ensure proper handling
              customArrows={
                // Only show arrows when not hovering or at final position
                (isHovering && !reachedFinalPosition) ? [] :
                  // Ensure we're passing a proper array of arrows
                  (Array.isArray(arrows) ? arrows : [])
              }
              // Squares highlighted in final position - ensure proper handling
              customSquareStyles={
                // Only show highlights when not hovering or at final position
                (isHovering && !reachedFinalPosition) ? {} :
                  // Ensure we're passing a proper object of highlights
                  (highlightedSquares && typeof highlightedSquares === 'object' ? highlightedSquares : {})
              }
              // Enable interactions for puzzles only
              areArrowsAllowed={false}
              arePiecesDraggable={isPuzzleGame}
              onPieceDrop={onPieceDrop}
              showBoardNotation={showCoordinates}
            />
          </div>
        </div>

        <div
          className="inside-grid-card"
          style={{
            width: `${boardWidth}px`,
            margin: '0 auto',
            gap: '10px',
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ flex: '1', position: 'relative', height: '50px' }}>
            {/* Create button for selected tag with animation - now non-clickable */}
            {selectedTag != null && (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="tag-display"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    color: isDifficultyHovered || isScoreHovered ? 'gold' : getTagStyle(selectedTag).color,
                    border: 'none',
                    background: isDifficultyHovered || isScoreHovered ? 'black' : getTagStyle(selectedTag).background,
                    fontSize: isTagHovered && isPuzzleGame && selectedTag === 'Puzzle' ? '16px' : '20px', // Smaller font for hover text
                    opacity: isTagChanging ? 0 : 1,
                    transform: isTagChanging ? 'translateY(10px)' : 'translateY(0)',
                    transition: `opacity ${TAG_ANIMATION_DURATION}ms ease-in-out, transform ${TAG_ANIMATION_DURATION}ms ease-in-out, font-size 0.2s ease`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '5px',
                    cursor: 'pointer', // Make it clear it's clickable
                    overflow: 'hidden', // Prevent text overflow
                  }}
                  onMouseEnter={handleTagMouseEnter}
                  onMouseLeave={handleTagMouseLeave}
                  onClick={handleTagClick}
                >
                  {/* Always show info icon for puzzles */}
                  {isPuzzleGame && selectedTag === 'Puzzle' && (
                    <div
                      style={{
                        position: 'relative',
                        left: '10px',
                        flexShrink: 0, // Prevent icon from shrinking
                        opacity: isPuzzleGame && selectedTag === 'Puzzle' && isTagHovered ? 0 : 1,
                      }}
                    >
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                  )}

                  {/* Create two text states with crossfade animation instead of conditional rendering */}
                  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    {/* Normal label exists but fades out when hovered */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      whiteSpace: 'nowrap',
                      opacity: (isPuzzleGame && selectedTag === 'Puzzle' && isTagHovered) ||
                        isDifficultyHovered ||
                        isScoreHovered ? 0 : 1,
                      transition: 'opacity 0.2s ease',
                      pointerEvents: 'none',
                    }}>
                      {getTagDisplay(selectedTag)}
                    </div>

                    {/* Show different content based on hover states */}
                    {isDifficultyHovered && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        whiteSpace: 'nowrap',
                        opacity: 1,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                        fontSize: '18px',
                        fontStyle: 'italic',
                        fontWeight: 'bold',
                      }}>
                        {DIFFICULTY[difficulty]?.label || difficulty}
                      </div>
                    )}

                    {/* Score hover text */}
                    {isScoreHovered && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        whiteSpace: 'nowrap',
                        opacity: 1,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                        fontSize: '18px',
                        fontStyle: 'italic',
                        fontWeight: 'bold',
                      }}>
                        Interactions
                      </div>
                    )}

                    {/* Expanded text for puzzle hover */}
                    {isPuzzleGame && selectedTag === 'Puzzle' && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        whiteSpace: 'nowrap',
                        opacity: isTagHovered && !isDifficultyHovered && !isScoreHovered ? 1 : 0,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                      }}>
                        Click To Expand
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className=""
            style={{
              color: 'black',
              fontSize: '18px',
              background: '#ffd700',
              width: '25%',
              padding: '8px 6px',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              height: '34px',
              whiteSpace: 'nowrap',
              justifyContent: 'space-around',
            }}

          >

            {/* Difficulty icon */}
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: 'black', // Change to gold
                color: 'gold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid black',
                fontWeight: 'bold',
                fontSize: '18px',
                cursor: 'pointer',
                zIndex: 11, // Increased z-index
              }}
              onMouseEnter={() => setIsDifficultyHovered(true)}
              onMouseLeave={() => setIsDifficultyHovered(false)}
            >
              {getDifficultyIcon(difficulty)}
            </div>

            <div
              style={{ marginLeft: '' }}
              onMouseEnter={() => setIsScoreHovered(true)}
              onMouseLeave={() => setIsScoreHovered(false)}
            >
              {adjustedScore}
            </div>
            {/* Game Score display (renamed from number) */}
            {/* {gameScore !== undefined && ( */}
            {/* )} */}
          </div>

          {/* Remove tooltip for score */}
          {/* Rating display (currently null but ready for future use) */}
          {/* {game?.rating !== undefined && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="fa-solid fa-chess-pawn"></i>
                  <span>{game.rating}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="fa-solid fa-chess-queen"></i>
                  <span>{game.rating}</span>
                </div>
              </>
            )} */}
        </div>
      </div>
    </div>
  );
};

export default GameSummary;
